import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext/AuthContext';
import AccessNotice from '../../../components/Portal/AccessNotice';
import WarmupPattern from './WarmupPattern';
import useWarmupCatalog from './useWarmupCatalog';
import { playPattern } from './warmupAudio';
import useWarmupPlayer from '../../../components/Warmups/useWarmupPlayer';
import WarmupPlayer from '../../../components/Warmups/WarmupPlayer';
import {
	PURPOSES,
	STYLE_WORDS,
	LEVEL_WORDS,
	sylText,
	matchesFilters,
	progressLine,
	exportText,
} from './warmupShape';
import './WarmupsPage.css';

/**
 * Warmups — the studio's warmup catalog, and the surface Grace reviews it on.
 *
 * Rebuilt from the standalone prototype at
 * `docs/refs/warmups/cyv-warmup-catalog-v2-review.html`: same grouping, same
 * filters, same synth, same export format. What changed is where it lives and
 * where the notes go — the prototype kept verdicts in `localStorage` on one
 * device, and these persist per reviewer in Firestore where Anthony can read
 * them. This is the long-term home of warmups; the review is its first job.
 *
 * STAFF ONLY, and the wall is the database's rather than this component's:
 * `firestore.rules` gates `warmupExercises` on `isEmployeeOrAdmin()`, so a
 * family account gets a denial here even if it somehow reached the route. The
 * route guard and the nav gate are the two cosmetic layers over that.
 */

const VERDICTS = [
	['keep', 'Keep'],
	['tweak', 'Change'],
	['cut', 'Cut'],
];

const AGE_BANDS = ['8-10', '11-13', '14-18'];

/**
 * One exercise row. Collapsed it is id + name + syllable + a verdict dot.
 *
 * PLAY uses the sampled piano (`player`, shared with the Routine mode so the
 * page holds one AudioContext) in its one-pass mode — the sampled successor
 * of the prototype's synth. The synth stays as the fallback for the one case
 * the samples cannot load, so a network failure still makes a sound.
 */
const WarmupRow = ({ exercise, review, saveState, isOpen, onToggle, onSave, player }) => {
	const [synthPlaying, setSynthPlaying] = useState(false);
	const playTimer = useRef(null);
	const stopRef = useRef(null);
	const [note, setNote] = useState(review?.note || '');
	const noteDirty = useRef(false);

	// Adopt the stored note when it changes underneath us (another device), but
	// never while this reviewer is mid-sentence — that would eat their typing.
	useEffect(() => {
		if (!noteDirty.current) setNote(review?.note || '');
	}, [review?.note]);

	useEffect(
		() => () => {
			clearTimeout(playTimer.current);
			if (stopRef.current) stopRef.current();
		},
		[]
	);

	const playSynth = useCallback(() => {
		// THE TAP IS THE GESTURE iOS REQUIRES. playPattern resumes the
		// AudioContext synchronously inside this handler — nothing may be
		// awaited before it, or Safari stops attributing the resume to the tap
		// and the first play is silent with no error.
		if (stopRef.current) stopRef.current();
		clearTimeout(playTimer.current);
		const handle = playPattern(exercise);
		if (!handle) return; // no Web Audio here; leave the button alone
		stopRef.current = handle.stop;
		setSynthPlaying(true);
		playTimer.current = setTimeout(() => setSynthPlaying(false), handle.durationMs);
	}, [exercise]);

	const samplerPlaying = Boolean(player) && player.state.exerciseId === exercise.id && player.state.status === 'playing';
	const playing = samplerPlaying || synthPlaying;

	const onPlay = useCallback(
		(e) => {
			e.stopPropagation();
			if (!player || player.loadError) {
				playSynth();
				return;
			}
			if (samplerPlaying) {
				player.engine.stop();
				return;
			}
			// ensureLoaded() calls engine.load() SYNCHRONOUSLY — the context is
			// created and resumed inside this tap — and resolves once the samples
			// have decoded. The play itself may follow the await: the context is
			// already running by then.
			player.ensureLoaded().then((ok) => {
				if (ok) player.engine.play(exercise, { startTonic: 60, once: true });
				else playSynth();
			});
		},
		[exercise, player, samplerPlaying, playSynth]
	);

	const verdict = review?.verdict || '';
	const state = saveState || '';

	return (
		<article className={`wu-row${isOpen ? ' is-open' : ''}`}>
			<button type="button" className="wu-row__head" onClick={onToggle} aria-expanded={isOpen}>
				<span className="wu-row__id">{exercise.id}</span>
				<span className="wu-row__title">
					<span className="wu-row__name">
						{exercise.name}
						{!exercise.was && <span className="wu-tag wu-tag--new">new</span>}
					</span>
					<span className="wu-row__syl">“{sylText(exercise)}”</span>
				</span>
				<span className={`wu-dot${verdict ? ` wu-dot--${verdict}` : ''}`} aria-hidden="true" />
				<i className={`fa-sharp fa-solid fa-chevron-${isOpen ? 'up' : 'down'} wu-row__chev`} aria-hidden="true" />
			</button>

			{isOpen && (
				<div className="wu-body">
					<div className="wu-playrow">
						<button type="button" className="ps-btn wu-play" onClick={onPlay}>
							<i className={`fa-sharp fa-solid fa-${playing ? 'volume-high' : 'play'}`} aria-hidden="true" />
							{player && player.loading ? 'Loading piano…' : playing ? (samplerPlaying ? 'Stop' : 'Playing') : 'Play'}
						</button>
						<span className="wu-playmeta">
							{exercise.bpm} bpm{exercise.artic && exercise.artic !== 'none' ? ` · ${exercise.artic}` : ''} · in C4, one pass
							{player && player.loadError ? ' · synth (piano did not load)' : ''}
						</span>
					</div>

					<WarmupPattern exercise={exercise} />

					<p className="wu-notes">{String(exercise.notes || '').replace(/^NEW\. /, '')}</p>

					<dl className="wu-facts">
						<dt>Syllable</dt>
						<dd>{Array.isArray(exercise.syl) ? exercise.syl.join(' ') : exercise.syl}</dd>
						<dt>Pattern</dt>
						<dd>{exercise.pattern.map((n) => n[0]).join(' ')}</dd>
						<dt>Level</dt>
						<dd>{LEVEL_WORDS[exercise.level] || exercise.level}</dd>
						<dt>Styles</dt>
						<dd>
							{String(exercise.styles || '')
								.split(' ')
								.map((s) => STYLE_WORDS[s] || s)
								.join(', ')}
						</dd>
						<dt>Ages</dt>
						<dd>{exercise.ages === 'all' ? '8–18' : String(exercise.ages || '').replace(/ /g, ', ')}</dd>
						{exercise.was ? (
							<>
								<dt>Draft 1</dt>
								<dd>{`was number ${exercise.was}`}</dd>
							</>
						) : null}
					</dl>

					<div className="wu-review">
						<div className="wu-seg" role="group" aria-label={`Verdict for ${exercise.id}`}>
							{VERDICTS.map(([value, label]) => (
								<button
									key={value}
									type="button"
									className={`wu-seg__btn wu-seg__btn--${value}${verdict === value ? ' is-on' : ''}`}
									aria-pressed={verdict === value}
									// Tapping the active verdict clears it. Cleared means
									// null, never a deleted doc — the note must survive.
									onClick={() => onSave({ verdict: verdict === value ? null : value }, { immediate: true })}>
									{label}
								</button>
							))}
						</div>
						<textarea
							className="ps-input wu-note"
							placeholder="What would you change — vowel, key, tempo, range, who it's for?"
							value={note}
							onChange={(e) => {
								noteDirty.current = true;
								setNote(e.target.value);
								onSave({ note: e.target.value });
							}}
							onBlur={() => {
								noteDirty.current = false;
							}}
						/>
						{/* Honest save state: "Saved" appears only once the write has
						    actually resolved (see useWarmupCatalog.flush). */}
						<span className={`wu-save wu-save--${state || 'idle'}`} role="status">
							{state === 'saving' && 'Saving…'}
							{state === 'saved' && 'Saved'}
							{state === 'error' && 'Not saved — try again'}
						</span>
					</div>
				</div>
			)}
		</article>
	);
};

const WarmupsPage = () => {
	const { currentUser, userProfile } = useAuth();
	const isAdmin = userProfile?.role === 'admin' || userProfile?.owner === true;
	const displayName =
		userProfile?.displayName || currentUser?.displayName || currentUser?.email?.split('@')[0] || '';

	const {
		catalog,
		isLoading,
		loadError,
		myReviews,
		saveReview,
		saveState,
		allReviews,
		allReviewsError,
		loadAllReviews,
	} = useWarmupCatalog({ uid: currentUser?.uid, displayName, isAdmin });

	const [purpose, setPurpose] = useState('');
	const [ages, setAges] = useState('');
	// 'catalog' — browse, hear, review. 'routine' — assemble and play a routine.
	// One sampler engine serves both; it is created here so the AudioContext
	// survives switching modes.
	const [mode, setMode] = useState('catalog');
	const player = useWarmupPlayer();
	const [q, setQ] = useState('');
	const [open, setOpen] = useState(() => new Set());
	const [showSummary, setShowSummary] = useState(false);
	const [copied, setCopied] = useState(false);

	const toggle = useCallback((id) => {
		setOpen((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	}, []);

	const groups = useMemo(
		() =>
			PURPOSES.map(([key, label, blurb]) => ({
				key,
				label,
				blurb,
				items: catalog.filter((c) => c.purpose === key && matchesFilters(c, { purpose, ages, q })),
			})).filter((g) => g.items.length > 0),
		[catalog, purpose, ages, q]
	);

	const onCopy = useCallback(async () => {
		const rows = {};
		catalog.forEach((c) => {
			rows[c.id] = (allReviews && allReviews[c.id]) || (myReviews[c.id] ? [myReviews[c.id]] : []);
		});
		const text = exportText(catalog, rows, { withNames: Boolean(allReviews) });
		try {
			await navigator.clipboard.writeText(text);
			setCopied(true);
			setTimeout(() => setCopied(false), 1500);
		} catch {
			// Clipboard is permission-gated and simply absent in some contexts.
			// A prompt is ugly and it is also the only thing that always works.
			// eslint-disable-next-line no-alert
			window.prompt('Copy these notes:', text);
		}
	}, [catalog, allReviews, myReviews]);

	const onOpenSummary = useCallback(() => {
		setShowSummary((s) => !s);
		if (!allReviews) loadAllReviews();
	}, [allReviews, loadAllReviews]);

	if (isLoading) {
		return (
			<div className="ps-page">
				<div className="ps-spinner" role="status" aria-label="Loading" />
			</div>
		);
	}

	if (loadError) {
		return (
			<div className="ps-page">
				<AccessNotice what="the warmup catalog" />
			</div>
		);
	}

	return (
		<div className="ps-page wu-page">
			<header className="wu-head">
				<div className="wu-head__titles">
					<h1>Warmups</h1>
					<p className="wu-sub">
						{catalog.length} exercises. Tap a row to hear it and leave a note — everything you mark
						is saved for you and visible to Anthony.
					</p>
				</div>
				<p className="wu-progress">{progressLine(myReviews, catalog.length)}</p>
			</header>

			<div className="wu-modes" role="tablist" aria-label="Warmups mode">
				<button type="button" role="tab" aria-selected={mode === 'catalog'} className={`wu-chip${mode === 'catalog' ? ' is-on' : ''}`} onClick={() => setMode('catalog')}>
					<i className="fa-sharp fa-solid fa-list" aria-hidden="true" /> Catalog
				</button>
				<button type="button" role="tab" aria-selected={mode === 'routine'} className={`wu-chip${mode === 'routine' ? ' is-on' : ''}`} onClick={() => setMode('routine')}>
					<i className="fa-sharp fa-solid fa-play" aria-hidden="true" /> Routine
				</button>
			</div>

			{mode === 'routine' && <WarmupPlayer catalog={catalog} player={player} />}

			{mode === 'catalog' && (
			<>
			<div className="wu-tools">
				<input
					type="search"
					className="ps-input wu-search"
					placeholder="Search name, syllable, ID"
					value={q}
					onChange={(e) => setQ(e.target.value)}
					aria-label="Search warmups"
				/>
				<select
					className="ps-select wu-ages"
					value={ages}
					onChange={(e) => setAges(e.target.value)}
					aria-label="Filter by age band">
					<option value="">All ages</option>
					{AGE_BANDS.map((a) => (
						<option key={a} value={a}>
							{a}
						</option>
					))}
				</select>
			</div>

			<div className="wu-chips" role="group" aria-label="Filter by purpose">
				<button
					type="button"
					className={`wu-chip${purpose === '' ? ' is-on' : ''}`}
					onClick={() => setPurpose('')}>
					All
				</button>
				{PURPOSES.map(([key, label]) => (
					<button
						key={key}
						type="button"
						className={`wu-chip${purpose === key ? ' is-on' : ''}`}
						onClick={() => setPurpose(key)}>
						{label}
					</button>
				))}
			</div>

			{isAdmin && (
				<div className="wu-adminbar">
					<button type="button" className="ps-btn wu-adminbtn" onClick={onOpenSummary}>
						<i className="fa-sharp fa-solid fa-list-check" aria-hidden="true" />
						{showSummary ? 'Back to the catalog' : 'Reviews summary'}
					</button>
					<button type="button" className="ps-btn wu-adminbtn" onClick={onCopy}>
						<i className="fa-sharp fa-solid fa-clipboard" aria-hidden="true" />
						{copied ? 'Copied' : 'Copy all notes as text'}
					</button>
				</div>
			)}

			{isAdmin && showSummary ? (
				<section className="wu-summary">
					{allReviewsError && <p className="ps-alert ps-alert--info">{allReviewsError}</p>}
					{!allReviews && !allReviewsError && <div className="ps-spinner" role="status" aria-label="Loading reviews" />}
					{allReviews &&
						catalog.map((c) => {
							const rows = allReviews[c.id] || [];
							return (
								<div key={c.id} className="wu-summary__row">
									<div className="wu-summary__id">{c.id}</div>
									<div className="wu-summary__name">{c.name}</div>
									<div className="wu-summary__verdicts">
										{rows.length === 0 && <span className="wu-summary__none">no review yet</span>}
										{rows.map((r) => (
											<span key={r.reviewerUid} className="wu-summary__one">
												<span className={`wu-dot${r.verdict ? ` wu-dot--${r.verdict}` : ''}`} aria-hidden="true" />
												<b>{r.updatedByName || 'Someone'}</b>
												{r.verdict ? ` — ${r.verdict}` : ''}
												{r.note ? `: ${r.note}` : ''}
											</span>
										))}
									</div>
								</div>
							);
						})}
				</section>
			) : (
				<>
					{groups.length === 0 && (
						<p className="wu-empty">Nothing matches. Clear the search or pick another group.</p>
					)}
					{groups.map((g) => (
						<section key={g.key} className="wu-group">
							<h2 className="wu-group__title">
								{g.label} <span className="wu-group__count">{g.items.length}</span>
							</h2>
							<p className="wu-group__blurb">{g.blurb}</p>
							{g.items.map((c) => (
								<WarmupRow
									key={c.id}
									exercise={c}
									review={myReviews[c.id]}
									saveState={saveState[c.id]}
									isOpen={open.has(c.id)}
									onToggle={() => toggle(c.id)}
									onSave={(patch, opts) => saveReview(c.id, patch, opts)}
									player={player}
								/>
							))}
						</section>
					))}
				</>
			)}
			</>
			)}
		</div>
	);
};

export default WarmupsPage;
